import React from 'react';
import { ChartBarIcon, ShieldCheckIcon, CurrencyDollarIcon, ExclamationTriangleIcon, HeartIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const GoatReports: React.FC = () => {
  const { user } = useAuth();

  // Admin-only access check
  if (user?.role !== 'admin') {
    return (
      <div className="container-mobile py-8">
        <div className="text-center">
          <ShieldCheckIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">This section requires admin privileges.</p>
          <Link 
            to="/dashboard" 
            className="inline-block text-blue-600 hover:text-blue-800 underline"
          >
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <ChartBarIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Goat Reports</h1>
        <p className="text-lg text-gray-600">
          Comprehensive reports and analytics for goat farming operations
        </p>
      </div>

      {/* Available Reports */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Financial & P&L Report */}
        <Link
          to="/goat/report/financials"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Financial & P&L Report</h3>
              <p className="text-sm text-gray-500">⭐ Critical Priority</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Complete Profit & Loss analysis with income, expenses, shed profitability, and monthly trends.
          </p>
        </Link>

        {/* Health & Vaccination Compliance Report */}
        <Link
          to="/goat/report/health-compliance"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Health & Vaccination</h3>
              <p className="text-sm text-gray-500">⭐ High Priority</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Vaccination compliance, deworming schedules, and treatment history reports.
          </p>
        </Link>

        {/* Breeding Performance Report */}
        <Link
          to="/goat/report/breeding-performance"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-pink-100 rounded-lg">
              <HeartIcon className="h-8 w-8 text-pink-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Breeding Performance</h3>
              <p className="text-sm text-gray-500">⭐ High Priority</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Breeding success rates, pregnancy tracking, and buck/doe performance analytics.
          </p>
        </Link>

        {/* Kidding & Productivity Report */}
        <Link
          to="/goat/report/kidding-productivity"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Kidding & Productivity</h3>
              <p className="text-sm text-gray-500">⭐ High Priority</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Kidding statistics, productivity metrics, and most productive does analysis.
          </p>
        </Link>

        {/* Population & Herd Management Report */}
        <Link
          to="/goat/report/population"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <ChartBarIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Population & Herd</h3>
              <p className="text-sm text-gray-500">Medium Priority</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Herd composition, age distribution, breed analysis, and shed utilization.
          </p>
        </Link>

        {/* Growth & Weight Tracking Report */}
        <Link
          to="/goat/report/growth"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-teal-100 rounded-lg">
              <ChartBarIcon className="h-8 w-8 text-teal-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Growth & Weight</h3>
              <p className="text-sm text-gray-500">Medium Priority</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Weight trends, growth rates, breed comparisons, and market readiness tracking.
          </p>
        </Link>

        {/* Mortality & Loss Analysis Report */}
        <Link
          to="/goat/report/mortality"
          className="card p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-500"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Mortality & Loss</h3>
              <p className="text-sm text-gray-500">Medium Priority</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Death records, mortality analysis, cause identification, and financial loss tracking.
          </p>
        </Link>
      </div>

      {/* Navigation Links */}
      <div className="flex justify-center space-x-4">
        <Link
          to="/goat/logs"
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          View Goat Logs
        </Link>
        <Link
          to="/dashboard"
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default GoatReports;
